/** This example uses ng-primitives styles, which are imported from ng-primitives/example-theme/index.css in the global styles file **/
import { UpperCasePipe } from '@angular/common';
import { Component, computed, input, model } from '@angular/core';
import { LucideCheck, LucideChevronDown, LucideGlobe, LucideDynamicIcon } from '@lucide/angular';
import {
  NgpSelect,
  NgpSelectDropdown,
  NgpSelectOption,
  NgpSelectPortal,
  provideSelectConfig,
} from 'ng-primitives/select';
import { SelectModel } from '../../forms/select/select_model';

@Component({
  selector: 'app-region-dropdown',
  imports: [NgpSelect, NgpSelectDropdown, NgpSelectOption, NgpSelectPortal, LucideDynamicIcon, UpperCasePipe],
  providers: [provideSelectConfig({ container: '.select-container' })],
  template: `
    <div [(ngpSelectValue)]="value" ngpSelect class="cursor-pointer">
      <svg [lucideIcon]="regionIcon" [size]="16" class="region-icon" />
      <div class="select-value-container">
        <span class="hidden md:block text-gray-500 text-xs font-normal leading-4">Region</span>
        @if (value(); as value) {
          <span class="select-value">{{ currentFlag() }} {{ value | uppercase }}</span>
        } @else {
          <span class="select-placeholder">Select an option</span>
        }
      </div>

      <div *ngpSelectPortal ngpSelectDropdown>
        @for (option of options(); track option.value) {
          <div
            [ngpSelectOptionValue]="option.value"
            [ngpSelectOptionDisabled]="option.value === value()"
            ngpSelectOption
            [attr.option-selected]="option.value === value() ? 'true' : 'false'"
            data-automationid="switchregion-dropdown-option"
            (click)="onOptionClick(option, $event)"
          >
            <span>{{ option.data.flag }} {{ option.name }}</span>
            @if (option.value === value()) {
              <svg [lucideIcon]="checkIcon" [size]="16" class="text-[#2C5AA0]" />
            }
          </div>
        } @empty {
          <div class="empty-message">No options found</div>
        }
      </div>
    </div>
  `,
  styles: `
    .select-container {
      position: relative;
    }

    [ngpSelect] {
      display: flex;
      align-items: center;
      height: 58px;
      width: 100%;
      padding-left: 12px;
      border-radius: 10px;
      border: 1px solid #d1d5dc;
      background-color: var(--ngp-background);
      box-shadow: var(--ngp-input-shadow);
      box-sizing: border-box;
    }

    @media (max-width: 768px) {
      [ngpSelect] {
        padding-left: 0;
        height: 40px;
        width: 40px;
        justify-content: center;
      }
    }

    [ngpSelect][data-focus] {
      outline: 2px solid var(--ngp-focus-ring);
      outline-offset: 2px;
    }

    .select-value,
    .select-placeholder {
      display: flex;
      align-items: center;
      flex: 1;
      background-color: transparent;
      color: var(--ngp-text-primary);
      font-family: inherit;
      font-size: 14px;
      height: 100%;
    }

    .select-value-container {
      padding-left: 8px;
    }

    @media (max-width: 768px) {
      .select-value,
      .select-placeholder {
        display: none;
      }

      .select-value-container {
        padding-left: 0;
      }
    }

    .select-placeholder {
      color: var(--ngp-text-secondary);
    }

    [ngpSelectDropdown] {
      border-radius: 8px;
      border: 1px solid rgba(0, 0, 0, 0.1);
      background: #fff;
      box-shadow:
        0 4px 6px -1px rgba(0, 0, 0, 0.1),
        0 2px 4px -2px rgba(0, 0, 0, 0.1);
      padding: 0.25rem;
      outline: none;
      position: absolute;
      animation: popover-show 0.1s ease-out;
      width: fit-content;
      min-width: 174px;
      box-shadow: var(--ngp-shadow-lg);
      box-sizing: border-box;
      margin-top: 4px;
      max-height: 240px;
      overflow-y: auto;
      z-index: 1001;
    }

    [ngpSelectDropdown][data-enter] {
      animation: select-show 0.1s ease-out;
    }

    [ngpSelectDropdown][data-exit] {
      animation: select-hide 0.1s ease-out;
    }

    [ngpSelectOption] {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      padding: 0.375rem 0.75rem;
      cursor: pointer;
      border-radius: 0.5rem;
      width: 100%;
      height: 36px;
      font-size: 14px;
      color: var(--ngp-text-primary);
      box-sizing: border-box;
    }

    [ngpSelectOption][data-hover] {
      background-color: #f0f0f0;
    }

    [ngpSelectOption][data-press] {
      background-color: #f0f0f0;
    }

    [ngpSelectOption][data-active] {
      background-color: #f0f0f0;
    }

    [ngpSelectOption][option-selected='true'] {
      background-color: #fff;
      opacity: 0.5;
      cursor: not-allowed;
    }

    .empty-message {
      display: flex;
      justify-content: center;
      align-items: center;
      padding: 0.5rem;
      color: var(--ngp-text-secondary);
      font-size: 14px;
      font-weight: 500;
      text-align: center;
    }

    .region-icon {
      color: #4a5565;
    }

    @keyframes select-show {
      0% {
        opacity: 0;
        transform: translateY(-10px) scale(0.9);
      }
      100% {
        opacity: 1;
        transform: translateY(0) scale(1);
      }
    }

    @keyframes select-hide {
      0% {
        opacity: 1;
        transform: translateY(0) scale(1);
      }
      100% {
        opacity: 0;
        transform: translateY(-10px) scale(0.9);
      }
    }
  `,
})
export default class RegionDropdownComponent {
  /** The options for the select. */
  readonly options = input<SelectModel[]>([]);

  /** The selected value. */
  readonly value = model<string | undefined | null>(undefined);
  readonly currentFlag = computed(() => this.options().find((option) => option.value === this.value())?.data.flag);
  readonly chevronDownIcon = LucideChevronDown;
  readonly regionIcon = LucideGlobe;
  readonly checkIcon = LucideCheck;

  onOptionClick(option: SelectModel, event: Event) {
    if (option.value === this.value()) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }
  }
}
